import React, { useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { ChartConfig } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface ChartVisualizationProps {
  config: ChartConfig;
  width?: string;
  height?: number;
}

const ChartVisualization: React.FC<ChartVisualizationProps> = ({ 
  config, 
  width = "100%", 
  height = 400 
}) => {
  const { isDarkMode } = useTheme();
  // const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handle plot ready/error events
  // const handlePlotReady = useCallback(() => {
  //   console.log('Plot ready callback triggered');
  //   setIsLoading(false);
  //   setError(null);
  // }, []);

  const handlePlotError = useCallback((err: any) => {
    // setIsLoading(false);
    setError(err?.message || 'Failed to render chart');
    console.error('Chart rendering error:', err);
    console.error('Error details:', JSON.stringify(err, null, 2));
  }, []);

  // Theme-aware color schemes using brand colors from tailwind.config.js
  const getColorScheme = () => {
    if (isDarkMode) {
      return [
        '#42B0D5', // primary
        '#6BC4E2', // primary.light
        '#34D399', // emerald-400
        '#FBBF24', // amber-400
        '#F87171', // red-400
        '#A78BFA', // violet-400
        '#FB7185', // rose-400
        '#38BDF8', // sky-400
        '#4ADE80', // green-400
        '#FACC15'  // yellow-400
      ];
    } else {
      return [
        '#42B0D5', // primary
        '#2A8CAA', // primary.dark
        '#10B981', // emerald-500
        '#F59E0B', // amber-500
        '#EF4444', // red-500
        '#8B5CF6', // violet-500
        '#EC4899', // pink-500
        '#06B6D4', // cyan-500
        '#22C55E', // green-500
        '#EAB308'  // yellow-500
      ];
    }
  };

  const colors = getColorScheme();

  // Theme-aware layout configuration following the design system
  const getThemeLayout = () => ({
    paper_bgcolor: 'rgba(0,0,0,0)', // Transparent background for theme switching
    plot_bgcolor: 'rgba(0,0,0,0)',  // Transparent plot area
    font: {
      color: isDarkMode ? '#E5E7EB' : '#374151', // dark.text / text.secondary
      family: 'Inter, system-ui, sans-serif',
      size: 12
    },
    title: {
      font: {
        color: isDarkMode ? '#F9FAFB' : '#111827', // dark.textPrimary / text.primary
        size: 16,
        family: 'Inter, system-ui, sans-serif'
      },
      x: 0.05,
      xanchor: 'left'
    },
    xaxis: {
      color: isDarkMode ? '#9CA3AF' : '#6B7280', // dark.textSecondary / text.tertiary
      gridcolor: isDarkMode ? '#374151' : '#E5E7EB', // dark.hover / neutral.dark
      zerolinecolor: isDarkMode ? '#4B5563' : '#D1D5DB', // slightly darker grid
    },
    yaxis: {
      color: isDarkMode ? '#9CA3AF' : '#6B7280', // dark.textSecondary / text.tertiary
      gridcolor: isDarkMode ? '#374151' : '#E5E7EB', // dark.hover / neutral.dark
      zerolinecolor: isDarkMode ? '#4B5563' : '#D1D5DB' // slightly darker grid
    },
    legend: {
      font: {
        color: isDarkMode ? '#E5E7EB' : '#374151' // dark.text / text.secondary
      }
    },
    hoverlabel: {
      bgcolor: isDarkMode ? '#1F2937' : '#FFFFFF', // dark.card / white
      bordercolor: isDarkMode ? '#374151' : '#D1D5DB', // dark.hover / neutral.dark
      font: {
        color: isDarkMode ? '#F9FAFB' : '#1F2937' // dark.textPrimary / text.primary
      }
    }
  });

  // Common Plotly configuration
  const getPlotlyConfig = () => ({
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: [
      'pan2d', 'lasso2d',
      'select2d', 'zoomIn2d', 'zoomOut2d', 
      'autoScale2d', 
      // 'resetScale2d', 
      'hoverClosestCartesian', 
      'hoverCompareCartesian', 'toggleSpikelines'
    ],
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png',
      filename: config.title || 'chart',
      height: height,
      width: 800,
      scale: 2
    }
  });

  // Convert legacy data format to Plotly data if GraphJSON not available
  const generatePlotlyData = () => {
    // If we have GraphJSON, enhance it with our theme
    if (config.graphJSON) {
      const themeLayout = getThemeLayout();
      const backendLayout = config.graphJSON.layout || {};
      
      // console.log('Backend layout from GraphJSON:', backendLayout);
      // console.log('Theme layout:', themeLayout);
      
      // Properly merge layouts: theme provides styling, backend provides content
      const mergedLayout = {
        ...themeLayout,
        ...backendLayout,
        title: {
          ...themeLayout.title,
          text: backendLayout.title || config.title || '',
          // Allow backend to override title styling if provided
          ...(backendLayout.title && typeof backendLayout.title === 'object' ? backendLayout.title : {}),
          ...themeLayout.title.font // Ensure theme font is applied
        },
        // Merge xaxis properly - preserve backend content with theme styling
        xaxis: {
          ...themeLayout.xaxis,
          ...backendLayout.xaxis
        },
        // Merge yaxis properly - preserve backend content with theme styling  
        yaxis: {
          ...themeLayout.yaxis,
          ...backendLayout.yaxis
        },
        font: {
          ...themeLayout.font
          // ...backendLayout.font
        },
        // Merge legend properly
        legend: {
          ...themeLayout.legend,
          ...backendLayout.legend,
          font: {
            ...themeLayout.legend?.font,
            ...backendLayout.legend?.font
          }
        }
      };
      
      console.log('Final merged layout:', mergedLayout);

      // Parse data safely if it's a string (coming from backend)
      let parsedData = config.graphJSON.data;
      if (typeof config.graphJSON.data === 'string') {
        console.log('Parsing GraphJSON data from string:', config.graphJSON.data);
        try {
          parsedData = JSON.parse(config.graphJSON.data);
          console.log('Successfully parsed data:', parsedData);
        } catch (e) {
          console.error('Failed to parse GraphJSON data:', e);
          parsedData = [];
        }
      } else {
        console.log('Data is already parsed:', parsedData);
      }

      const enhancedGraphJSON = {
        ...config.graphJSON,
        data: parsedData,
        layout: mergedLayout,
        config: {
          ...getPlotlyConfig(),
          ...config.graphJSON.config, // Allow backend config to override if needed
          modeBarButtonsToRemove: [
            ...(getPlotlyConfig().modeBarButtonsToRemove || []),
            ...(config.graphJSON.config?.modeBarButtonsToRemove || [])
          ] // Ensure we always remove these buttons
        }
      };
      
      // Ensure data traces use brand colors if not already themed
      if (enhancedGraphJSON.data && Array.isArray(enhancedGraphJSON.data)) {
        console.log('Original traces before color processing:', enhancedGraphJSON.data);
        enhancedGraphJSON.data = enhancedGraphJSON.data.map((trace: any, index: number) => {
          console.log(`Processing trace ${index}:`, trace);
          const processedTrace = {
            ...trace,
            // Apply brand colors if trace doesn't have colors or has generic ones
            ...(trace.type === 'bar' && !trace.marker?.color && {
              marker: {
                ...trace.marker,
                color: colors[index % colors.length],
                line: { color: 'rgba(0,0,0,0.1)', width: 1 }
              }
            }),
            ...(trace.type === 'scatter' && !trace.marker?.color && {
              marker: { 
                ...trace.marker, 
                color: colors[index % colors.length],
                size: trace.marker?.size || (trace.mode?.includes('markers') ? 8 : 6)
              },
              line: {
                ...trace.line,
                color: colors[index % colors.length],
                width: trace.line?.width || 2
              }
            }),
            ...(trace.type === 'pie' && !trace.marker?.colors && {
              marker: {
                ...trace.marker,
                colors: colors.slice(0, trace.values?.length || colors.length)
              }
            })
          };
          console.log(`Processed trace ${index}:`, processedTrace);
          return processedTrace;
        });
        console.log('Final processed traces:', enhancedGraphJSON.data);
      }
      
      return enhancedGraphJSON;
    }

    // Fallback: convert legacy data format
    if (!config.data || config.data.length === 0) {
      return { data: [], layout: {}, config: {} };
    }

    const baseLayout = {
      ...getThemeLayout(),
      title: config.title || '',
      showlegend: true,
      margin: { l: 50, r: 20, t: 60, b: 50 }
    };

    let plotData: any[] = [];

    switch (config.type) {
      case 'bar':
        plotData = [{
          type: 'bar',
          x: config.data.map(item => item[config.x_axis!]),
          y: config.data.map(item => item[config.y_axis!]),
          marker: {
            color: colors[0],
            line: {
              color: 'rgba(0,0,0,0.1)',
              width: 1
            }
          },
          hovertemplate: `%{x}<br>%{y:,.0f}<extra></extra>`
        }];
        break;

      case 'line':
        plotData = [{
          type: 'scatter',
          mode: 'lines+markers',
          x: config.data.map(item => item[config.x_axis!]),
          y: config.data.map(item => item[config.y_axis!]),
          line: {
            color: colors[0],
            width: 2
          },
          marker: {
            color: colors[0],
            size: 6
          },
          hovertemplate: `%{x}<br>%{y:,.0f}<extra></extra>`
        }];
        break;

      case 'area':
        plotData = [{
          type: 'scatter',
          mode: 'lines',
          fill: 'tonexty',
          x: config.data.map(item => item[config.x_axis!]),
          y: config.data.map(item => item[config.y_axis!]),
          line: {
            color: colors[0],
            width: 2
          },
          fillcolor: colors[0].replace(')', ',0.3)').replace('rgb', 'rgba'),
          hovertemplate: `%{x}<br>%{y:,.0f}<extra></extra>`
        }];
        break;

      case 'pie':
        plotData = [{
          type: 'pie',
          labels: config.data.map(item => item[config.x_axis!]),
          values: config.data.map(item => item[config.y_axis!]),
          marker: {
            colors: colors.slice(0, config.data.length)
          },
          textinfo: 'label+percent',
          textposition: 'outside',
          hovertemplate: `%{label}<br>%{value:,.0f} (%{percent})<extra></extra>`
        }];
        baseLayout.showlegend = false;
        break;

      case 'scatter':
        plotData = [{
          type: 'scatter',
          mode: 'markers',
          x: config.data.map(item => item[config.x_axis!]),
          y: config.data.map(item => item[config.y_axis!]),
          marker: {
            color: colors[0],
            size: 8,
            opacity: 0.7
          },
          hovertemplate: `%{x}<br>%{y:,.0f}<extra></extra>`
        }];
        break;

      default:
        plotData = [];
    }

    return {
      data: plotData,
      layout: baseLayout,
      config: getPlotlyConfig()
    };
  };

  // Main render logic
  if (!config) {
    return (
      <div className={`p-8 text-center rounded-lg border ${
        isDarkMode 
          ? 'border-dark-border bg-dark-secondary text-dark-textSecondary' 
          : 'border-gray-200 bg-gray-50 text-gray-500'
      }`}>
        No chart configuration available
      </div>
    );
  }

  // Handle 'none' type - don't render anything
  if (config.type === 'none') {
    return null;
  }

  // // Handle table type separately
  // if (config.type === 'table') {
  //   return (
  //     <div className={`rounded-lg border p-4 ${
  //       isDarkMode 
  //         ? 'border-dark-border bg-dark-card' 
  //         : 'border-gray-200 bg-white'
  //     }`}>
  //       {config.title && (
  //         <h3 className={`text-lg font-semibold mb-4 ${
  //           isDarkMode ? 'text-dark-textPrimary' : 'text-gray-800'
  //         }`}>
  //           {config.title}
  //         </h3>
  //       )}
  //       {renderTable()}
  //     </div>
  //   );
  // }

  // Check if we have valid data for visualization
  console.log('ChartVisualization config:', config);
  console.log('GraphJSON data:', config.graphJSON?.data);
  console.log('Legacy data:', config.data);
  
  // Helper to check if data is valid (handles both array and JSON string)
  const checkValidData = (data: any) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) && parsed.length > 0;
      } catch {
        return false;
      }
    }
    return false;
  };
  
  const hasValidData = checkValidData(config.graphJSON?.data) || checkValidData(config.data);
     
  console.log('hasValidData:', hasValidData);

  if (!hasValidData) {
    return null;
  }

  // Generate Plotly specification
  const plotlySpec = generatePlotlyData();
  console.log('Generated plotlySpec:', plotlySpec);
  console.log('plotlySpec.data:', plotlySpec.data);
  console.log('plotlySpec.layout:', plotlySpec.layout);
  console.log('About to render Plot component with data:', JSON.stringify(plotlySpec.data, null, 2));

  // Ensure data is always an array for the Plot component
  const plotData = Array.isArray(plotlySpec.data) ? plotlySpec.data : [];
  if (plotData.length === 0) {
    console.warn('Plot data is empty after processing');
  }

  // Show loading state
  // if (isLoading) {
  //   return (
  //     <div className={`rounded-lg border p-8 flex items-center justify-center ${
  //       isDarkMode 
  //         ? 'border-dark-border bg-dark-card text-dark-textSecondary' 
  //         : 'border-gray-200 bg-white text-gray-500'
  //     }`} style={{ width, height }}>
  //       <div className="flex flex-col items-center">
  //         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-current mb-2"></div>
  //         <div className="text-sm">Loading chart...</div>
  //       </div>
  //     </div>
  //   );
  // }

  // Show error state
  if (error) {
    return (
      <div className={`rounded-lg border p-8 text-center ${
        isDarkMode 
          ? 'border-red-800 bg-red-900/20 text-red-300' 
          : 'border-red-200 bg-red-50 text-red-600'
      }`} style={{ width, height }}>
        <div className="text-lg mb-2">Chart Error</div>
        <div className="text-sm">{error}</div>
        <button 
          onClick={() => {
            setError(null);
            // setIsLoading(true);
          }}
          className={`mt-4 px-4 py-2 rounded text-sm ${
            isDarkMode
              ? 'bg-red-800 hover:bg-red-700 text-white'
              : 'bg-red-100 hover:bg-red-200 text-red-800'
          }`}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${
      isDarkMode 
        ? 'border-dark-border bg-dark-card' 
        : 'border-gray-200 bg-white'
    }`}>
      <div style={{ width, height }}>
        <Plot
          key={`plot-${isDarkMode ? 'dark' : 'light'}-${JSON.stringify(plotData).slice(0, 100)}`} // Force re-render on theme/data change
          data={plotData}
          layout={{
            ...plotlySpec.layout,
            // Only override layout properties that are essential for responsive behavior
            autosize: true,
            height: height,
            // Preserve all other layout properties including title, axis labels, etc.
          }}
          config={plotlySpec.config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
          // onInitialized={handlePlotReady}
          // onUpdate={handlePlotReady}
          onError={handlePlotError}
        />
      </div>
    </div>
  );
};

export default ChartVisualization;
